import { useState, useEffect } from "react";
import Calendar from "../components/Calendar";
import Modal from "../components/Modal";
import AddPostModal from "../components/AddPostModal";
import { Task } from "../types";
import { useTask } from '../context/TaskContext';
import { createTask, deleteTask, getTasks, updateTask } from "../api/taskApi";
import Layout from '../components/Layout';
import logo from '../assets/logo_scheduler.png';
import './Home.css';
import { ConfirmModal } from '../components/ConfirmModal';
import { getFullUrl } from '../utils/urlUtils';

export default function Home() {
  const [tasks, setTasks] = useState<Task[]>([]);
  const { selectedTask, setSelectedTask } = useTask();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isAddPostModalOpen, setIsAddPostModalOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [images, setImages] = useState<string[]>([]);
  const [showModalConfirmDelete, setShowModalConfirmDelete] = useState(false);
  const PUBLISH_POST_FACEBOOK = 1;
  const PUBLISH_POST_INSTAGRAM = 2;
  const PUBLISH_STORY_FACEBOOK = 3;
  const PUBLISH_STORY_INSTAGRAM = 4;

  useEffect(() => {
    const fetchTasks = async () => {
      const fetchedTasks = await getTasks();
      setTasks(fetchedTasks);
    };

    fetchTasks();
  }, []);

  const handleAddModifyTask = async (task: Task) => {
    setSelectedTask(null);
    setIsModalOpen(false);

    if (task.id !== -1) {
      await updateTask(task);
      setTasks((prevTasks) => {
        const taskIndex = prevTasks.findIndex((t) => t.id === task.id);
        const updatedTasks = [...prevTasks];
        updatedTasks[taskIndex] = task;
        return updatedTasks;
      });
    } else {
      const response = await createTask(task);
      if (response && response.id) {
        setTasks((prevTasks) => [...prevTasks, { ...task, id: response.id }]);
      }
    }
  };

  const handleMoveTask = (taskId: number, newDate: Date) => {
    const task = tasks.find(task => task.id === taskId);
    if (!task) return;

    updateTask({ ...task, datetimePlanned: newDate });

    setTasks(tasks.map(task =>
      task.id === taskId ? { ...task, datetimePlanned: newDate } : task
    ));
  };

  const handleTaskSelect = (task: Task) => {
    setSelectedTask(task);
    setIsModalOpen(true);
  };

  const handleDayClick = (date: Date) => {
    setSelectedDate(date);
    setIsAddPostModalOpen(true);
  };

  const onModifyTask = (task: Task | null) => {
    if (task) {
      setSelectedDate(task.datetimePlanned);
    }
    setIsAddPostModalOpen(true);
  };

  const onDeleteTask = (task: Task | null) => {
    if (task) {
      setShowModalConfirmDelete(true);
    }
  };

  const handleConfirmDelete = () => {
    if (selectedTask) {
      deleteTask(selectedTask);
      setTasks((prevTasks) => prevTasks.filter(t => t.id !== selectedTask.id));
      setSelectedTask(null);
      setIsModalOpen(false);
      setShowModalConfirmDelete(false);
    }
  };

  const onLogout = () => {
    localStorage.removeItem('authToken');
    window.location.href = getFullUrl('login');
  };

  const handleImageClick = (image: string) => {
    setSelectedImage(image);
  };

  return (
    <Layout
      logoSrc={logo}
      appName="Scheduler App"
      onLogout={onLogout}
    >
      <div className="calendar-container">
        <Calendar
          tasks={tasks}
          onTaskAdd={handleDayClick}
          onTaskMove={handleMoveTask}
          onTaskSelect={handleTaskSelect}
        />
        <div className="image-picker-container">
          {images.map((image) => (
            <div
              key={image}
              className={`image-picker ${selectedImage === image ? 'selected' : ''}`}
              onClick={() => handleImageClick(image)}
            >
              <img src={image} alt="Selectable" />
              <div className="tick">✔</div>
            </div>
          ))}
        </div>
        <Modal
          isOpen={isModalOpen}
          onClose={() => { setSelectedTask(null); setIsModalOpen(false); }}
          title={selectedTask?.actionLabel}
          onModify={() => onModifyTask(selectedTask)}
          onDelete={() => onDeleteTask(selectedTask)}
        >
          {selectedTask && selectedTask.article.id && (
            <div>
              <i style={{ fontSize: '16px' }}>
                (
                {selectedTask.datetimePublished ? 'Fue ' : 'Será '}
                publicado el día {selectedTask.datetimePlanned.toLocaleDateString()} a las {selectedTask.datetimePlanned.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })})
              </i>
              {
                // todo: unhardcode this
                (
                  parseInt(selectedTask.actionId) === PUBLISH_STORY_FACEBOOK ||
                  parseInt(selectedTask.actionId) === PUBLISH_STORY_INSTAGRAM) &&
                <div>
                  <div
                  >
                    <img src={selectedTask.article?.image_url} alt="Story Image" className="modal-image" id="image-container" />
                  </div>
                </div>
              }
              {
                (
                  parseInt(selectedTask.actionId) === PUBLISH_POST_FACEBOOK ||
                  parseInt(selectedTask.actionId) === PUBLISH_POST_INSTAGRAM) &&
                <div>

                  <h2 style={{ paddingTop: '10px' }}>{selectedTask.article.title || "No Title"}</h2>
                  <div className="modal-image-container">
                    <img src={selectedTask.article.image_url} alt="Article Image" className="modal-image" />
                  </div>
                  <span>{'content' in selectedTask.article ? selectedTask.article.content : "No Content Available"}</span>
                </div>
              }
            </div>
          )}
        </Modal>
        <AddPostModal
          isOpen={isAddPostModalOpen}
          selectedDate={selectedDate}
          selectedTask={selectedTask}
          onClose={() => setIsAddPostModalOpen(false)}
          onSave={(task) => {
            if (task.datetimePlanned) {
              handleAddModifyTask({ ...task, datetimePlanned: task.datetimePlanned });
            }
            setIsAddPostModalOpen(false);
          }}
        />
        <ConfirmModal
          show={showModalConfirmDelete}
          title="Borrar tarea"
          text={`¿Confirma la eliminación de la tarea "${selectedTask?.actionLabel || ''}"?`}
          onClose={() => {
            setShowModalConfirmDelete(false);
          }}
          onConfirm={handleConfirmDelete}
        />
      </div>
    </Layout>
  );
}